import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaFacebook, FaInstagram, FaWhatsapp, FaMapMarkerAlt, FaClock, FaPaperPlane, FaUser, FaHeading, FaCommentAlt, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { MdEmail, MdLocationOn, MdPhone, MdAccessTime } from 'react-icons/md';
import OurBranches from '../components/common/OurBranches';
import WhatsAppButton from '../components/common/WhatsAppButton';

const Contact = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    // Read query parameter and pre-fill subject
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const service = params.get('service');
        if (service) {
            setFormData(prev => ({
                ...prev,
                subject: `Application for ${service}`
            }));
        }
    }, [location]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const formDataToSubmit = {
                ...formData,
                _subject: `New Contact Message: ${formData.subject}`,
                _template: 'table',
                _captcha: 'false'
            };

            const response = await fetch("https://formsubmit.co/ajax/choundeshwaripatasanstha@gmail.com", {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(formDataToSubmit)
            });

            if (response.ok) {
                setSubmitStatus('success');
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                });
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };


    const contactInfo = [
        {
            icon: <FaMapMarkerAlt />,
            title: t('contact.info.address'),
            content: t('footer.address'),
            link: "https://www.google.com/maps/search/?api=1&query=17.089167,74.458"
        },
        {
            icon: <FaPhone />,
            title: t('contact.info.phone'),
            content: t('footer.phone'),
            link: `tel:${t('footer.phone')}`
        },
        {
            icon: <FaEnvelope />,
            title: t('contact.info.email'),
            content: t('footer.email'),
            link: `mailto:${t('footer.email')}`
        },
        {
            icon: <FaClock />,
            title: t('contact.info.hours'),
            content: t('contact.info.hoursContent'),
            link: null
        }
    ];

    const socialLinks = [

        { icon: <FaFacebook />, name: 'Facebook', link: 'https://www.facebook.com/profile.php?id=61556303297385', color: 'hover:text-blue-600' },
        { icon: <FaInstagram />, name: 'Instagram', link: 'https://www.instagram.com/shreechoundeshwaripatsanstha/', color: 'hover:text-pink-600' },
        { icon: <FaWhatsapp />, name: 'WhatsApp', link: 'https://wa.me/919823162163', color: 'hover:text-green-600' }
    ];

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
            {/* 1. Hero Section - Corporate Style */}
            <section className="relative py-24 bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-4 block">{t('contact.badge')}</span>
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">{t('contact.title')}</h1>
                    <div className="w-24 h-1 bg-secondary mx-auto mb-6"></div>
                    <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
                        {t('contact.subtitle')}
                    </p>
                </div>
            </section>

            {/* 2. Contact Info Cards */}
            <section className="py-16 bg-white relative -mt-10 z-20 mx-4 md:mx-0">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {contactInfo.map((info, index) => (
                            <div key={index} className="bg-white p-8 rounded-xl shadow-xl border-t-4 border-secondary text-center hover:-translate-y-2 transition-transform duration-300">
                                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center text-primary text-3xl mx-auto mb-6">
                                    {info.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{info.title}</h3>
                                {info.link ? (
                                    <a href={info.link} className="text-gray-600 hover:text-primary transition-colors text-sm leading-relaxed block break-all">
                                        {info.content}
                                    </a>
                                ) : (
                                    <p className="text-gray-600 text-sm leading-relaxed">{info.content}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3. Contact Form & Context */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        {/* Left Column: Form */}
                        <div>
                            <div className="mb-10">
                                <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-2 block">{t('contact.form.badge')}</span>
                                <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('contact.form.title')}</h2>
                                <p className="text-gray-600">{t('contact.form.description')}</p>
                            </div>

                            <form onSubmit={handleFormSubmit} className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                            <FaUser className="text-secondary" /> {t('contact.form.name')} *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder={t('contact.form.namePlaceholder')}
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:border-primary focus:ring-0 focus:bg-white transition-all outline-none"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <MdEmail className="text-secondary" /> {t('contact.form.email')} *
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder={t('contact.form.emailPlaceholder')}
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:border-primary focus:ring-0 focus:bg-white transition-all outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <MdPhone className="text-secondary" /> {t('contact.form.phone')} *
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                placeholder={t('contact.form.phonePlaceholder')}
                                                value={formData.phone}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:border-primary focus:ring-0 focus:bg-white transition-all outline-none"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                            <FaHeading className="text-secondary" /> {t('contact.form.subject')} *
                                        </label>
                                        <input
                                            type="text"
                                            name="subject"
                                            placeholder={t('contact.form.subjectPlaceholder')}
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:border-primary focus:ring-0 focus:bg-white transition-all outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                            <FaCommentAlt className="text-secondary" /> {t('contact.form.message')}
                                        </label>
                                        <textarea
                                            name="message"
                                            placeholder={t('contact.form.messagePlaceholder')}
                                            value={formData.message}
                                            onChange={handleChange}
                                            rows="5"
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:border-primary focus:ring-0 focus:bg-white transition-all outline-none resize-none"
                                        ></textarea>
                                    </div>

                                    {/* Status Messages */}
                                    {submitStatus === 'success' && (
                                        <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                                            <FaCheckCircle /> {t('contact.form.success')}
                                        </div>
                                    )}
                                    {submitStatus === 'error' && (
                                        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                                            <FaTimesCircle /> {t('contact.form.error')}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('contact.form.sending')}
                                            </>
                                        ) : (
                                            <>
                                                <FaPaperPlane /> {t('contact.form.submit')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Right Column: Connect & Map */}
                        <div className="space-y-12">
                            {/* Social Connect */}
                            <div>
                                <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-2 block">{t('contact.social.badge')}</span>
                                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.social.title')}</h3>
                                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
                                    <p className="text-gray-600 mb-8">{t('contact.social.description')}</p>
                                    <div className="flex gap-4 jsutify-start">
                                        {socialLinks.map((social, index) => (
                                            <a
                                                key={index}
                                                href={social.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`w-14 h-14 bg-gray-50 rounded-xl flex items-center justify-center text-2xl text-gray-600 transition-all duration-300 hover:bg-white hover:shadow-md ${social.color}`}
                                                aria-label={social.name}
                                            >
                                                {social.icon}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Map Placeholder or Embed */}
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.map.title')}</h3>
                                <div className="bg-gray-200 rounded-2xl h-80 w-full overflow-hidden shadow-inner relative">
                                    <iframe
                                        src="https://www.google.com/maps/embed?pb=!1m13!1m8!1m3!1d7051.371374661987!2d74.4566034!3d17.0873479!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTfCsDA1JzIxLjMiTiA3NMKwMjcnMjguOCJF!5e1!3m2!1sen!2sin!4v1771651202521!5m2!1sen!2sin"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        allowFullScreen=""
                                        loading="lazy"
                                        title="Location Map"
                                        className="grayscale hover:grayscale-0 transition-all duration-500"
                                    ></iframe>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <OurBranches />
            <WhatsAppButton />
        </div>
    );
};

export default Contact;
